
import NativeClient from "@/client/native-client";
import { ShoppingListRecommendationBannerListItem } from "@/types/shoppingList";
import ShoppingListRecommendationListItem from "./ShoppingListRecommendationListItem.vue";
import ShoppingListRecommendationPageIndicator from "@/components/shoppingList/ShoppingListRecommendationPageIndicator.vue";
import { defineComponent, SetupContext, ref, computed, PropType, ComponentPublicInstance } from "vue";

export default defineComponent({
  name: "ShoppingListRecommendationList",
  components: { ShoppingListRecommendationListItem, ShoppingListRecommendationPageIndicator },
  props: {
    /** 提案一覧 */
    recommendationList: {
      type: Array as PropType<ShoppingListRecommendationBannerListItem[]>,
      required: true
    }
  },
  setup(props, context: SetupContext) {
    const selectIndex = ref(0);
    interface HTMLElementEvent<T extends HTMLElement> extends Event {
      target: T;
    }
    type HTMLDivElementEvent = HTMLElementEvent<HTMLDivElement>;

    class ShoppingListPageIndicatorListItem {
      index: number;
      isSelected: boolean;
      foodId: number;
      constructor() {
        this.index = 0;
        this.isSelected = false;
        this.foodId = 0;
      }
    }
    const indicatorList = computed(() => {
      const result: Array<ShoppingListPageIndicatorListItem> = [];
      props.recommendationList.forEach((element, index) => {
        const indicator = new ShoppingListPageIndicatorListItem();
        indicator.index = index;
        indicator.foodId = element.foodId;
        if (selectIndex.value == index) {
          indicator.isSelected = true;
        } else {
          indicator.isSelected = false;
        }
        result.push(indicator);
      });
      return result;
    });

    // スクロール関連
    let timerId: number | null = null;
    let isTouching = false;
    let isScrolling = false;
    const scrollElement = ref<ComponentPublicInstance<HTMLInputElement>>();
    const setScrollTo = (position: number, isSmooth: boolean) => {
      NativeClient.sendDebugLog("ShoppingListRecommendationList setScrollTo scrollWidth = " + scrollElement.value?.scrollWidth);
      if (scrollElement.value != undefined) {
        const elements = document.getElementsByClassName("shoppinglist-recommendation-list-scrollarea-contents-item");
        const items = Array.from(elements);
        const scrollPoint = ((scrollElement.value!.scrollWidth - 32) / items.length) * position;
        NativeClient.sendDebugLog("ShoppingListRecommendationList setScrollTo left = " + scrollPoint);
        if (isSmooth) {
          scrollElement.value!.scrollTo({ top: 0, left: scrollPoint, behavior: 'smooth' });
        } else {
          scrollElement.value!.scrollTo({ top: 0, left: scrollPoint});
        }
      }
    };

    const onRecommendScrollEnd = () => {
      NativeClient.sendDebugLog("ShoppingListRecommendationList onRecommendScrollEnd");
      timerId = null;
      isScrolling = false;
      if (!isTouching && NativeClient.isIOS()) {
        NativeClient.sendDebugLog("ShoppingListRecommendationList onRecommendScrollEnd setScroll Position = " + selectIndex.value);
        setScrollTo(selectIndex.value, true);
      }
    };

    const recommendScroll = (event: HTMLDivElementEvent) => {
      NativeClient.sendDebugLog("ShoppingListRecommendationList recommendScroll");
      isScrolling = true;
      if (timerId != null) {
        clearTimeout(timerId);
        timerId = null;
      }
      timerId = window.setTimeout(onRecommendScrollEnd, 50);
    };

    // スクロール監視
    let startX = 0;
    let startY = 0;
    let moveX = 0;
    let lastX = 0;
    let startCurrentScrollLeft = 0;

    // スクロール制御。デフォルトのスクロール動作（慣性スクロール）を抑止して、コードによるスクロールを行う
    const touchStart = (e: TouchEvent) => {
      isTouching = true;
      startX = e.touches[0].pageX;
      startY = e.touches[0].pageY;

      // デフォルトのスクロール動作停止
      e.preventDefault();

      NativeClient.sendDebugLog("ShoppingListRecommendationList touchStart startX =" + startX );
      if (scrollElement.value != undefined) {
        startCurrentScrollLeft = scrollElement.value!.scrollLeft
        //NativeClient.sendDebugLog("ShoppingListRecommendationList touchStart scrollPosition = " + scrollElement.value?.scrollLeft);
      }
    };
    const touchMove = (e: TouchEvent) => {
      moveX = e.touches[0].pageX - startX;
      lastX = e.touches[0].pageX;
      const leftPosition = startCurrentScrollLeft - moveX;
      const screenWidth = window.innerWidth
      if (scrollElement.value != undefined && leftPosition > 0 && Math.abs(moveX) < screenWidth) {
        scrollElement.value!.scrollTo({ top: 0, left: leftPosition });
      }
      context.emit("move-y", (e.touches[0].pageY - startY))
      //NativeClient.sendDebugLog("ShoppingListRecommendationList touchMove moveX = " + moveX + ", lastX = " + lastX);
    };
    const touchEnd = (e: TouchEvent) => {
      isTouching = false;
      e.preventDefault();
      NativeClient.sendDebugLog("ShoppingListRecommendationList touchEnd moveX =" + moveX + ", lastX = " + lastX);
      if (scrollElement.value != undefined) {
        // スクロール領域の情報取得
        const scrollRect = scrollElement.value!.getBoundingClientRect();
        const scrollHorizontalCenter = (scrollRect.width - scrollRect.left) / 2;

        // スクロール内部の要素の取得
        const elements = document.getElementsByClassName("shoppinglist-recommendation-list-scrollarea-contents-item");
        const items = Array.from(elements);
        for (let index = 0; index < items.length; index ++) {
          const clientRect = items[index].getBoundingClientRect();
          //NativeClient.sendDebugLog("ShoppingListRecommendationList touchEnd element[" + index + "] left = " + clientRect.left + ". width = " + clientRect.width);
          if (clientRect.left < scrollHorizontalCenter && scrollHorizontalCenter <= clientRect.right) {
            //NativeClient.sendDebugLog("ShoppingListRecommendationList touchEnd hit");
            selectIndex.value = index;
            break;
          }
        }
      }
      if (!isScrolling || !NativeClient.isIOS()) {
        setScrollTo(selectIndex.value, true);
      }
      moveX = 0;
      startX = 0;
      window.setTimeout(() => {
        // スクロール動作停止解除
        e.preventDefault();
      }, 50);
    };

    // 選択
    let isSending = false;
    const selectFood = (foodId: number, name: string, recipeId: number) => {
      NativeClient.sendDebugLog("ShoppingListRecommendationList select foodId = " + foodId + ", foodName = " + name + ", recipeId = " + recipeId);
      if (isSending) {
        return;
      }
      isSending = true;
      context.emit("click-recommendation", foodId, name, recipeId);
      window.setTimeout(() => {
        isSending = false;
      }, 100);
    };
    const selectRecipe = (recipeId: number) => {
      NativeClient.sendDebugLog("ShoppingListRecommendationList select recipe = " + recipeId);
      if (isSending) {
        return;
      }
      isSending = true;
      context.emit("click-recommendation-recipe", recipeId);
      window.setTimeout(() => {
        isSending = false;
      }, 100);
    }

    return {
      indicatorList,
      scrollElement,
      recommendScroll,
      touchStart,
      touchMove,
      touchEnd,
      selectFood,
      selectRecipe
    };
  }
});
